import type { TableProps } from 'antd';
import { Radar } from 'lucide-react';

import { DataTable, Tile, TileHeader } from '../../../components/shared';
import { Button, Empty } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { GIO_IP_MONITOR } from '../constants';
import { useIpMonitor } from '../hooks/useBaoMat';
import type { FormCaiDat } from '../hooks/useFormCaiDat';
import { giaTriHienTai } from '../nghiep-vu/cai-dat';
import { chuanHoaCidr, ipThuocCidr } from '../nghiep-vu/cidr';
import { danhSachCuaGiaTri, docDanhSachIp, themVaoGiaTri } from '../nghiep-vu/danh-sach-ip';
import type { BaoCaoIpMonitor as BaoCao } from '../types';

type Dong = BaoCao['items'][number];

/**
 * Báo cáo IP ngoài dải tin cậy trong 72 giờ (ADR-0059 mục 6.3, ADR-0090 mục 1, ADR-0098).
 *
 * Ngoài dải không bị chặn, chỉ chịu trần request thông thường; báo cáo cho Tenant Admin thấy mạng nào của tổ chức
 * còn thiếu trong dải tin cậy. Mỗi dòng có lối tắt "Thêm vào dải tin cậy" đưa IP vào bản nháp — chưa lưu, vẫn qua
 * thanh lưu và hộp xác nhận như mọi thay đổi khác. Tổ chức chưa có dải tin cậy thì không IP nào bị coi là ngoài dải.
 */
export function BaoCaoIpMonitor({ tenantId, form }: { tenantId: string; form: FormCaiDat }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const q = useIpMonitor(tenantId);

  const mAllow = form.policy?.settings.find((m) => m.key === 'network.ip_allowlist');
  const giaTri = mAllow ? giaTriHienTai(mAllow, form.nhap) : [];
  // Bản nháp còn dòng sai là chuỗi: vẫn đọc các dòng hợp lệ để biết IP nào đã có
  const allowlist =
    danhSachCuaGiaTri(giaTri) ??
    docDanhSachIp(typeof giaTri === 'string' ? giaTri : '').muc.map((m) => ({ cidr: m.cidr }));
  const daLuu = mAllow?.value;
  const daLuuCoDai = Array.isArray(daLuu) && daLuu.length > 0;
  const suaDuoc = Boolean(mAllow?.editable) && !form.chiDoc;

  const themVaoAllowlist = (ip: string) => {
    const kq = chuanHoaCidr(ip);
    if (!kq.ok) return;
    form.doi('network.ip_allowlist', themVaoGiaTri(giaTri, kq.cidr, t('baoMat.ip.ghiChuTuBaoCao')));
  };

  const cot: TableProps<Dong>['columns'] = [
    {
      title: t('baoMat.ip.cotIp'),
      dataIndex: 'ip',
      width: 220,
      render: (v: string) => <span className="break-all font-mono text-xs text-content-primary">{v}</span>,
    },
    {
      title: t('baoMat.ip.cotSoLan'),
      dataIndex: 'count',
      width: 110,
      align: 'right',
      render: (v: number) => <span className="text-xs tabular-nums text-content-secondary">{soVN(v, locale)}</span>,
    },
    {
      title: t('baoMat.ip.cotNguoiDung'),
      dataIndex: 'user_ids',
      width: 140,
      render: (v: string[]) => (
        <span className="text-xs text-content-secondary" title={v.join(', ')}>
          {t('baoMat.ip.soNguoiDung', { so: soVN(v.length, locale) })}
        </span>
      ),
    },
    {
      title: t('baoMat.ip.cotLanCuoi'),
      dataIndex: 'last_seen_at',
      width: 150,
      render: (v: string) => <span className="whitespace-nowrap text-xs text-content-muted">{ngayGioVN(v, locale)}</span>,
    },
    ...(suaDuoc
      ? [
          {
            title: <span className="sr-only">{t('chung.thaoTac')}</span>,
            width: 170,
            // Một hành động ở mọi dòng: nút trần có nhãn (ADR-UI-0005).
            render: (_v: unknown, row: Dong) =>
              allowlist.some((d) => ipThuocCidr(row.ip, d.cidr)) ? (
                <span className="text-xs text-content-muted">{t('baoMat.ip.daCoTrongAllowlist')}</span>
              ) : (
                <Button variant="outline" size="sm" onClick={() => themVaoAllowlist(row.ip)}>
                  {t('baoMat.ip.themVaoAllowlist')}
                </Button>
              ),
          },
        ]
      : []),
  ];

  return (
    <Tile className="overflow-hidden">
      <TileHeader
        icon={<Radar size={16} />}
        tone="primary"
        title={t('baoMat.ip.baoCaoTieuDe', { so: soVN(GIO_IP_MONITOR, locale) })}
      />
      {!daLuuCoDai && (
        <p className="border-b border-default px-5 py-2.5 text-xs text-content-muted">
          {t('baoMat.ip.chuaCoDaiGhiChu')}
        </p>
      )}
      {q.error ? (
        <div role="alert" className="flex items-center justify-between gap-3 px-5 py-4">
          <span className="text-sm text-danger-700">{t('baoMat.ip.loi', { loi: dichLoi(q.error) })}</span>
          <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
            {t('chung.thuLai')}
          </Button>
        </div>
      ) : (
        <DataTable<Dong>
          caption={t('baoMat.ip.baoCaoTieuDe', { so: soVN(GIO_IP_MONITOR, locale) })}
          rowKey="ip"
          dataSource={q.data?.items ?? []}
          columns={cot}
          loading={q.isLoading}
          size="small"
          pagination={false}
          locale={{
            emptyText: q.isLoading ? (
              ' '
            ) : (
              <Empty icon={Radar} title={t('baoMat.ip.baoCaoRong')} description={t('baoMat.ip.baoCaoRongMo')} />
            ),
          }}
        />
      )}
    </Tile>
  );
}
